import isUndefined from 'lodash/isUndefined'
import { isLinkValid, validateDateHelper, cleanLink } from 'handsontable/utils'
import { UrlAudits } from 'handsontable/utils/codes.url-audits'
import { validateList } from './list/listValidator'

export function validateCell(
  templateField,
  rowNum,
  fieldId,
  templateFieldsById,
  dependentFields,
  valueObj,
  data,
  dataContainer,
) {
  if (!templateField) {
    return
  }
  const validationErrors = []
  const warnings = []
  let value = valueObj.cell_value
  const isVariable = templateField.field.segment_type === 'variable'
  const governanceOwner = isVariable ? templateField : templateField.field
  const governanceType = governanceOwner.governance_type

  // validate base options before we manipulate the value at all
  validateBaseOptions()

  switch (governanceType) {
    case 'cv_id':
      break
    case 'autofill':
      break
    case 'concatenation':
      break
    case 'constant':
      value = value || governanceOwner.constant_value
      validateConstant(value, governanceOwner.constant_value)
      break
    case 'list': {
      const feedback = { errors: validationErrors, warnings }
      validateList(valueObj, governanceOwner, dependentFields, rowNum, data, feedback)
      value = value || ''
      break
    }
    case 'text':
      validateText(value, governanceOwner.validation)
      break
    case 'link':
      validateLink(value, governanceOwner.link_governance)
      break
    case 'link-validation': {
      // Link Status field
      const linkValue = dataContainer.getDataForRowAndColumn(rowNum, valueObj.metadata.linkField)
      if (!isEmptyRow(rowNum, dataContainer, templateFieldsById)) {
        const templateId = Object.values(templateFieldsById).find(tf => {
          return tf.template_id
        }).template_id
        validateLinkStatus(
          dataContainer,
          rowNum,
          governanceOwner.link_governance,
          Object.values(templateFieldsById),
          templateId,
          linkValue,
        )
        return
      }
      break
    }
    case 'date':
      if (!governanceOwner.date.auto_populate) {
        validateDate(value, governanceOwner)
      }
      break
    case 'shortened link':
      break
    case 'qr-code':
      break
  }

  // add validation errors to the cell metadata so that we can grab it later
  // this is not ideal... the errors should be calculated outside of the renderer.
  // but since we don't have time to refactor its right now, we'll just go with it.
  if (valueObj?.metadata) {
    valueObj.metadata.validationErrors = validationErrors
    valueObj.metadata.warnings = warnings
  }

  // what do we actually want to return here?
  return { validationErrors, warnings }

  function validateBaseOptions() {
    if (
      (governanceOwner.required || templateField.required) &&
      !value &&
      !governanceOwner?.date?.auto_populate
    ) {
      validationErrors.push('This field is required')
    } else if (value === null) {
      return
    }

    validateLength()

    function validateLength() {
      if (!value) {
        return
      }

      const hasMinLength = !!governanceOwner.min_length
      const hasMaxLength = !!governanceOwner.max_length
      const valueMustBeExactLength =
        hasMinLength && hasMaxLength && governanceOwner.max_length === governanceOwner.min_length

      if (value.length > 4096) {
        validationErrors.push(`This field cannot be more than 4096 characters long.`)
      }
      if (valueMustBeExactLength && governanceOwner.max_length !== value.length) {
        validationErrors.push(
          `This field must be exactly ${governanceOwner.max_length} characters.`,
        )
      }
      if (
        !valueMustBeExactLength &&
        governanceOwner.max_length &&
        value.length > governanceOwner.max_length
      ) {
        validationErrors.push(
          `This field must be no more than ${governanceOwner.max_length} characters.`,
        )
      }
      if (
        !valueMustBeExactLength &&
        governanceOwner.min_length &&
        value.length < governanceOwner.min_length
      ) {
        validationErrors.push(
          `This field must contain at least ${governanceOwner.min_length} characters.`,
        )
      }
    }
  }

  function validateText(value, validationOptions) {
    if (!value || !validationOptions) {
      return
    }

    if (validationOptions.begins_with) {
      const passesBeginsWithTest = value.startsWith(validationOptions.begins_with)
      if (!passesBeginsWithTest) {
        validationErrors.push(`Value must begin with ${validationOptions.begins_with}`)
      }
    }

    if (validationOptions.ends_with) {
      const passesEndsWithTest = value.endsWith(validationOptions.ends_with)
      if (!passesEndsWithTest) {
        validationErrors.push(`Value must end with ${validationOptions.ends_with}`)
      }
    }

    if (validationOptions.contains) {
      const passesContainsTest = value.includes(validationOptions.contains)
      if (!passesContainsTest) {
        validationErrors.push(`Value must contain ${validationOptions.contains}`)
      }
    }

    if (validationOptions.does_not_contain) {
      let passesDoesNotContainTest = true
      if (validationOptions.exclude_as_string) {
        passesDoesNotContainTest = !value.includes(validationOptions.does_not_contain)
      } else {
        // we have to text every string.
        const checks = validationOptions.does_not_contain.split('')
        checks.forEach(checker => {
          if (value.includes(checker)) {
            passesDoesNotContainTest = false
          }
        })
      }
      if (!passesDoesNotContainTest) {
        validationErrors.push(`Value must not contain ${validationOptions.does_not_contain}`)
      }
    }

    if (validationOptions.flag_new) {
      const valueIsNew =
        governanceOwner.previous_values && governanceOwner.previous_values.indexOf(value) === -1
      if (valueIsNew) {
        warnings.push(`This value has not been submitted previously.`)
      }
    }

    if (validationOptions.prohibit_letters) {
      const valueHasLetters = /[a-z]/gi.test(value)
      if (valueHasLetters) {
        validationErrors.push(`Value must not contain letters`)
      }
    }
    if (validationOptions.prohibit_numbers) {
      const valueHasNumbers = /[0-9]/g.test(value)
      if (valueHasNumbers) {
        validationErrors.push(`Value must not contain numbers`)
      }
    }
    if (validationOptions.prohibit_spaces) {
      const valueHasSpaces = /\s/g.test(value)
      if (valueHasSpaces) {
        validationErrors.push(`Value must not contain spaces`)
      }
    }
    if (validationOptions.prohibit_symbols) {
      const valueHasSymbols = /[^a-zA-Z0-9 ]/g.test(value)
      if (valueHasSymbols) {
        validationErrors.push(`Value must not contain symbols`)
      }
    }
  }
  function validateConstant(value, constant) {
    if (!value) {
      return
    }

    if (constant !== value) {
      validationErrors.push(`Value must be ${constant}`)
    }
  }

  function validateDate(inputDate, field) {
    const errors = validateDateHelper(inputDate, field)
    if (errors && errors.length > 0) {
      errors.forEach(e => {
        validationErrors.push(e)
      })
    }
  }

  function validateLink(value, linkGovernance) {
    // The link must be parsable to be considered valid
    const attemptedLinkParse = cleanLink(value)
    if (value && !attemptedLinkParse.parsable) {
      validationErrors.push(
        `The following error occurred when</br>processing the link:</br>${attemptedLinkParse.message}`,
      )
      return
    }

    if (value && linkGovernance.domain_restrictions.length) {
      if (!isLinkValid(value, linkGovernance)) {
        const allowedDomains = linkGovernance.domain_restrictions.map(
          restriction => restriction.domain,
        )
        const domainList = allowedDomains.map(domain => `<li>${domain}</li>`).join('')
        validationErrors.push(
          `Landing Page must be one of the following domains: <ul>${domainList}</ul>`,
        )
      }
    }
  }

  function validateLinkStatus(
    dataContainer,
    rowNum,
    linkGovernance,
    templateFields,
    templateId,
    url,
  ) {
    UrlAudits.getLinkStatusAndRefresh({
      dataContainer,
      rowNo: rowNum,
      targets: linkGovernance.targets,
      templateColumns: templateFields,
      templateId,
      url,
    })
  }
}

export function isEmptyRow(row, dataContainer, templateFields) {
  const IGNORED_GOVERNANCE_TYPES = {
    constant: true,
    concatenation: true,
    autofill: true,
    'link-validation': true,
  }

  let isEmpty = true

  // look over all cols of row
  Object.values(templateFields).forEach(col => {
    const value =
      dataContainer.data[row][col.field_id] && dataContainer.data[row][col.field_id].cell_value
    if (value !== '' && value !== null && !isUndefined(value)) {
      // value is set, so lookup the internals
      // this works because the metadata base is the template_field
      if (
        IGNORED_GOVERNANCE_TYPES[col.governance_type] ||
        IGNORED_GOVERNANCE_TYPES[col.field.governance_type] ||
        col.default_value
      ) {
        // easier to read this way logically. Niether it's tempalte field or field have bad govnernace types.
      } else {
        // if it was defined and not a constant or object, then it's obviously not empty
        isEmpty = false
      }
    }
  })
  // if we made it through all the cells, then we should be empty
  return isEmpty
}

export function validateRow(row, columns, dataContainer) {
  const emptyRow = isEmptyRow(row, dataContainer, dataContainer.allRenderColumns)
  Object.values(dataContainer.allRenderColumns).forEach(col => {
    const required = !!col.required || !!col.field.required
    if (emptyRow) {
      // if the row is empty, just set everything to not have validation errors and move on.
      if (dataContainer.data[row][col.field_id]) {
        dataContainer.data[row][col.field_id].metadata.validationErrors = []
      }
      return
    }
    if (required && !col?.field?.date?.auto_populate) {
      const colId = col.field_id
      const hasValue = !!dataContainer.data[row][colId].cell_value
      if (!hasValue) {
        const currentErrors = dataContainer.data[row][colId].metadata.validationErrors
        // this doesn't belong here but I can't find a place for it.
        dataContainer.data[row][colId].metadata.row = row
        if (currentErrors) {
          if (!currentErrors.includes('This field is required')) {
            currentErrors.push('This field is required')
          }
        } else {
          dataContainer.data[row][colId].metadata.validationErrors = ['This field is required']
        }
      } else {
        dataContainer.data[row][colId].metadata.validationErrors = dataContainer.data[row][
          colId
        ].metadata.validationErrors.filter(er => {
          return er != 'This field is required'
        })
      }
    }

    const isLinkField = col.governance_type === 'link'
    if (isLinkField) {
      validateLinkParsing(row, col, dataContainer)
    }
  })
}

/**
 * This function needs to check for link parsing errors to populate the dataContainer so that the errors are displayed properly.
 * @param row the current row being checked.
 * @param col the current column, which should be a link column.
 * @param dataContainer the dataContainer which needs to have the validation errors updated if there are errors parsing.
 */
function validateLinkParsing(row, col, dataContainer) {
  const cell = dataContainer.data[row][col.field_id]
  const cellValue = cell.cell_value
  if (!cellValue) return
  const linkParseResult = cleanLink(cellValue)
  if (!linkParseResult.parsable) {
    const currentErrors = cell.metadata.validationErrors
    if (currentErrors.length > 0) {
      // Filter out any existing parsing errors
      const errors = currentErrors.filter(error => {
        return !error.includes('processing the link')
      })
      if (errors.length > 0) {
        cell.metadata.validationErrors.push(errors)
      }
    } else {
      cell.metadata.validationErrors.push(
        `The following error occurred when</br>processing the link:</br>${linkParseResult.message}`,
      )
    }
  } else {
    const currentErrors = cell.metadata.validationErrors
    // Filter out any existing parsing errors
    const errors = currentErrors.filter(error => {
      return !error.includes('processing the link')
    })
    cell.metadata.validationErrors = errors
  }
}
